/**
 * useServerAdmin Hook
 * Standardized API hooks for server administration and system monitoring
 *
 * Refactored: Phase 3.6 - Factory Pattern Migration
 * Pattern: Uses createQueryHookNoParams and createMutationHook factories
 * Previous: Manual useQuery/useMutation with apiClient from '../api/index'
 * Current: Factory-based hooks with built-in logging and invalidation
 *
 * Migration Benefits:
 * - Reduced code duplication (~60% less code)
 * - Standardized logging patterns with consistent context
 * - Centralized query invalidation logic
 * - Type-safe configuration with proper error handling
 * - Consistent API client usage via useApiClient()
 */

import { log } from '@/utils/errors/logger';
import { QUERY_STALE_TIME } from '@/constants/queryConfig';
import { createQueryHookNoParams, createMutationHook } from '@/hooks/api/factories';
import { useQueryClient } from '@tanstack/react-query';
import type {
  SystemStatusResponse,
  SystemCleanupResponse,
  SMTPConfigurationResponse,
  TestEmailRequest,
} from '@/api/data-contracts';

// ============================================================================
// Type Aliases for Exported API Types
// ============================================================================

// Re-export API types for better discoverability
export type SystemStatus = SystemStatusResponse;
export type SMTPConfig = SMTPConfigurationResponse;
export type DuplicateACLResponse = SystemCleanupResponse;

// ============================================================================
// Query Keys
// ============================================================================

export const serverAdminQueryKeys = {
  all: ['server-admin'] as const,
  systemStatus: () => [...serverAdminQueryKeys.all, 'system-status'] as const,
  smtpConfig: () => [...serverAdminQueryKeys.all, 'smtp-config'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get system status including database, Redis, and Qdrant health.
 * Uses SHORT stale time (30s) for real-time monitoring.
 * Includes refetchInterval for auto-refresh every minute.
 *
 * Returns:
 * - status: Overall system status
 * - version: API version
 * - uptime: System uptime
 * - database_connected: MongoDB connection status
 * - redis_connected: Redis connection status
 * - qdrant_connected: Qdrant connection status
 * - total_users: Number of users in the system
 * - total_documents: Number of documents in the system
 *
 * Usage:
 * @example
 * const { data: status, isLoading } = useGetSystemStatus();
 * console.log(status.database_connected); // boolean
 * console.log(status.total_documents); // number
 *
 * // With custom refetch interval
 * const { data: status } = useGetSystemStatus({
 *   queryOptions: { refetchInterval: 30 * 1000 } // 30 seconds
 * });
 */
export const useGetSystemStatus = createQueryHookNoParams<SystemStatus>({
  queryKey: () => serverAdminQueryKeys.systemStatus(),
  queryFn: (apiClient) =>
    apiClient.getSystemStatusApiServerAdminStatusGet().then(response => {
      // Enhanced logging with connection statuses
      log.api.response('GET', '/api/server-admin/status', 200, {
        database_connected: response.data.database_connected,
        redis_connected: response.data.redis_connected,
        qdrant_connected: response.data.qdrant_connected,
        total_documents: response.data.total_documents
      });
      return response.data;
    }),
  logContext: 'GET /api/server-admin/status',
  staleTime: QUERY_STALE_TIME.SHORT, // 30 seconds - real-time monitoring
  // Note: refetchInterval can be added via queryOptions when using the hook
});

/**
 * Hook to get SMTP configuration from database or environment.
 * Uses LONG stale time (5 minutes) as SMTP config rarely changes.
 *
 * Returns:
 * - status: Configuration status
 * - host: SMTP server hostname
 * - port: SMTP server port
 * - use_tls: Whether TLS is enabled
 * - from_email: Sender email address
 * - from_name: Sender display name
 *
 * Usage:
 * @example
 * const { data: smtpConfig } = useGetSMTPConfig();
 * console.log(smtpConfig.status); // 'configured' | 'not_configured'
 * console.log(smtpConfig.host); // 'smtp.gmail.com'
 */
export const useGetSMTPConfig = createQueryHookNoParams<SMTPConfig>({
  queryKey: () => serverAdminQueryKeys.smtpConfig(),
  queryFn: (apiClient) =>
    apiClient.getSmtpConfigApiServerAdminConfigSmtpGet().then(response => {
      // Log SMTP config status
      log.api.response('GET', '/api/server-admin/config/smtp', 200, {
        status: response.data.status,
        host: response.data.host
      });
      return response.data;
    }),
  logContext: 'GET /api/server-admin/config/smtp',
  staleTime: QUERY_STALE_TIME.LONG, // 5 minutes - SMTP config rarely changes
});

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to save SMTP configuration to database.
 * Invalidates SMTP config query on success.
 *
 * Usage:
 * @example
 * const saveSMTP = useSaveSMTPConfig();
 * await saveSMTP.mutateAsync({
 *   host: 'smtp.gmail.com',
 *   port: 587,
 *   user: 'user@example.com',
 *   password: 'password',
 *   from_email: 'noreply@example.com',
 *   use_tls: true,
 *   from_name: 'SmartDoc AI'
 * });
 */
export const useSaveSMTPConfig = createMutationHook<SMTPConfig, Record<string, string | number | boolean>>({
  mutationFn: (apiClient, smtpConfig) =>
    apiClient.saveSmtpConfigApiServerAdminConfigSmtpPost(smtpConfig).then(response => {
      // Log successful save with minimal details (no credentials)
      log.api.response('POST', '/api/server-admin/config/smtp', 200, { success: true });
      return response.data;
    }),
  invalidateQueries: [
    serverAdminQueryKeys.smtpConfig(),
  ],
  logContext: 'POST /api/server-admin/config/smtp',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('smtp_config_saved', 'ServerAdmin');
  },
});

/**
 * Hook to send a test email using current SMTP configuration.
 * Validates SMTP settings by sending a test email to specified address.
 *
 * Usage:
 * @example
 * const sendTest = useSendTestEmail();
 * await sendTest.mutateAsync({ email: 'test@example.com' });
 */
export const useSendTestEmail = createMutationHook<unknown, TestEmailRequest>({
  mutationFn: (apiClient, params) =>
    apiClient.testSmtpConfigApiAdminSmtpTestPost(params).then(response => {
      log.api.response('POST', '/api/server-admin/config/smtp/test', 200, { success: true });
      return response.data;
    }),
  invalidateQueries: [],
  logContext: 'POST /api/server-admin/config/smtp/test',
  logMethod: 'POST',
  onSuccess: (_data, params) => {
    log.user.action('smtp_test_email_sent', 'ServerAdmin', { email: params.email });
  },
});

/**
 * Hook to delete SMTP configuration from database (revert to environment).
 * Invalidates SMTP config query on success.
 *
 * Usage:
 * @example
 * const deleteSMTP = useDeleteSMTPConfig();
 * await deleteSMTP.mutateAsync();
 */
export const useDeleteSMTPConfig = createMutationHook<unknown, void>({
  mutationFn: (apiClient) =>
    apiClient.deleteSmtpConfigApiAdminSmtpConfigDelete().then(response => {
      log.api.response('DELETE', '/api/server-admin/config/smtp', 200, { success: true });
      return response.data;
    }),
  invalidateQueries: [
    serverAdminQueryKeys.smtpConfig(),
  ],
  logContext: 'DELETE /api/server-admin/config/smtp',
  logMethod: 'DELETE',
  onSuccess: () => {
    log.user.action('smtp_config_deleted', 'ServerAdmin');
  },
});

/**
 * Hook to cleanup all data (MongoDB, Qdrant, S3).
 * DANGEROUS: Deletes all documents, vectors, and files.
 * Invalidates system status on success.
 *
 * Usage:
 * @example
 * const cleanup = useCleanupAllData();
 * if (confirm('Delete all data?')) {
 *   await cleanup.mutateAsync();
 * }
 */
export const useCleanupAllData = createMutationHook<SystemCleanupResponse, void>({
  mutationFn: (apiClient) =>
    apiClient.cleanupAllDataApiServerAdminCleanupAllPost().then(response => {
      log.api.response('POST', '/api/server-admin/cleanup/all', 200, { success: true });
      return response.data;
    }),
  invalidateQueries: [
    serverAdminQueryKeys.systemStatus(),
  ],
  logContext: 'POST /api/server-admin/cleanup/all',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('data_cleanup_all', 'ServerAdmin');
  },
});

/**
 * Hook to fix duplicate ACL entries in the database.
 * Removes duplicate access control list entries.
 * Invalidates system status on success.
 *
 * Usage:
 * @example
 * const fixACL = useFixDuplicateACL();
 * const result = await fixACL.mutateAsync();
 * console.log(`Removed ${result.duplicates_removed} duplicates`);
 */
export const useFixDuplicateACL = createMutationHook<DuplicateACLResponse, void>({
  mutationFn: (apiClient) =>
    apiClient.fixDuplicateAclEntriesApiServerAdminFixDuplicateAclPost().then(response => {
      // Log number of duplicates removed
      log.api.response('POST', '/api/server-admin/fix/duplicate-acl', 200, {
        mongodb_deleted: response.data.mongodb_deleted,
        qdrant_deleted: response.data.qdrant_deleted,
        s3_deleted: response.data.s3_deleted,
      });
      return response.data;
    }),
  invalidateQueries: [
    serverAdminQueryKeys.systemStatus(),
  ],
  logContext: 'POST /api/server-admin/fix/duplicate-acl',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('duplicate_acl_fixed', 'ServerAdmin', {
      mongodb_deleted: data.mongodb_deleted,
      qdrant_deleted: data.qdrant_deleted,
      s3_deleted: data.s3_deleted,
    });
  },
});

/**
 * Hook to recreate a Qdrant collection.
 * DANGEROUS: Deletes and recreates the specified collection, losing all vectors.
 * Invalidates system status on success.
 *
 * Usage:
 * @example
 * const recreate = useRecreateQdrantCollection();
 * await recreate.mutateAsync('smartdoc_documents');
 */
export const useRecreateQdrantCollection = createMutationHook<unknown, string>({
  mutationFn: (apiClient, collectionName) =>
    apiClient.recreateQdrantCollectionApiServerAdminRecreateQdrantCollectionNamePost(collectionName).then(response => {
      log.api.response('POST', `/api/server-admin/recreate-qdrant/${collectionName}`, 200, { success: true });
      return response.data;
    }),
  invalidateQueries: [
    serverAdminQueryKeys.systemStatus(),
  ],
  logContext: 'POST /api/server-admin/recreate-qdrant/:collectionName',
  logMethod: 'POST',
  onSuccess: (_data, collectionName) => {
    log.user.action('qdrant_collection_recreated', 'ServerAdmin', { collection: collectionName });
  },
});

/**
 * Hook to manually refresh system status.
 * This is a special mutation that doesn't call the API directly,
 * but instead invalidates and refetches the system status query.
 *
 * Usage:
 * @example
 * const refresh = useRefreshSystemStatus();
 * await refresh.mutateAsync();
 */
export const useRefreshSystemStatus = (): {
  mutate: () => void;
  mutateAsync: () => Promise<void>;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
} => {
  const queryClient = useQueryClient();

  // This is a special case that doesn't use the factory because it doesn't call the API
  // Instead, it just invalidates and refetches queries
  const mutation = createMutationHook<void, void>({
    mutationFn: async () => {
      log.user.action('system_status_refresh', 'ServerAdmin');
      await queryClient.invalidateQueries({ queryKey: serverAdminQueryKeys.systemStatus() });
      await queryClient.refetchQueries({ queryKey: serverAdminQueryKeys.systemStatus() });
    },
    invalidateQueries: [], // No invalidation needed - we handle it manually
    logContext: 'REFRESH system-status',
    onError: () => {
      log.error('Failed to refresh system status', { component: 'ServerAdmin' });
    },
  });

  return mutation();
};
